<template>
  <v-dialog
    v-if="!isVectorRecruiting"
    v-model="isModalOpen"
    content-class="fixed-height-md"
    max-width="500"
    @keydown.esc="closeModal"
  >
    <modal-card
      :title="modalTitle"
      full
      fixed
      class="command-modal"
      @close="closeModal"
    >
      <!-- Back button when navigating subcommands -->
      <template #headerActionsLeft>
        <v-btn fab icon small v-if="parentCommand" @click="reset">
          <v-icon color="light">chevron_left</v-icon>
        </v-btn>
      </template>

      <div class="command-content">
        <!-- Search Input -->
        <div class="command-content__search">
          <v-text-field
            v-model="searchQuery"
            @keydown.up.prevent="navigate(-1)"
            @keydown.down.prevent="navigate(1)"
            @keydown.enter.prevent="executeSelectedCommand"
            @paste="handlePaste"
            autofocus
            autocomplete="off"
            outlined
            height="10"
            class="command-search no-label"
            :placeholder="searchPlaceholder"
          />

          <v-progress-linear
            v-if="loading"
            height="2"
            indeterminate
            color="primary"
          />
        </div>

        <!-- Command List -->
        <div class="command-content__list">
          <div
            v-for="cmd in filteredCommands"
            :key="cmd.id"
            @mouseenter="selectCommand(cmd)"
            @click="executeCommand(cmd)"
          >
            <content-line
              :text="resolveCommandTitle(cmd)"
              :icon="cmd.icon"
              :font-icon="cmd.fontIcon"
              :tooltip="Boolean(cmd.tooltipText)"
              :tooltip-text="cmd.tooltipText"
              class="command"
              :class="commandClasses(cmd)"
            >
              <template v-if="cmd.badge" #customIcon>
                <div class="content-line__icon">
                  <icon-badge
                    :icon="cmd.badge.icon"
                    :color="cmd.badge.color"
                    :size="24"
                  />
                </div>
              </template>
              <template v-slot:actions v-if="cmd.actions">
                <component
                  :is="cmd.actions"
                  :cmd="cmd"
                  :ref="cmd.id"
                  @close="closeModal"
                />
              </template>
            </content-line>
          </div>
        </div>
      </div>
    </modal-card>
  </v-dialog>
</template>

<script>
import clone from 'lodash/clone';
import { mapGetters } from 'vuex';
import EventBus from '@/factories/eventBus';
import { setCommandInstance } from './kit';
import { navigation } from '@/components/layouts/sidebar/sidebarLinks.js';
import IconBadge from '@/components/basic/icon-badge/IconBadge';

// Command Definitions
import Command from './command';
import CreateContact from './createContact';
import CreateTask from './createTask';
import OpenChatBot from './openChatBot';
import SwitchTeams from './switchTeams';
import OmniSearch from './omniSearch';

// Static Texts
const COMMAND_TITLE = 'ConveYour Commands';
const SEARCH_PLACEHOLDER = 'Search AI, Commands and Records (navigate ↓↑)';

const registeredCommands = [
  OpenChatBot,
  CreateContact,
  CreateTask,
  SwitchTeams,
];

export default {
  name: 'CommandModal',
  components: {
    IconBadge,
  },

  data() {
    return {
      modalTitle: COMMAND_TITLE,
      searchPlaceholder: SEARCH_PLACEHOLDER,
      isModalOpen: false,
      searchQuery: '',
      selectedCommand: null,
      parentCommand: null,
      commands: [],
      loading: false,
      tempCommands: [], // Used for route-specific or temporary commands
      searchHandler: null,
    };
  },

  computed: {
    ...mapGetters('users', ['currentUser']),
    filteredCommands() {
      return this.getFilteredCommands();
    },
  },

  watch: {
    isModalOpen(open) {
      if (open) {
        this.prepareCommands();
        this.$emit('shown');
        this.$nextTick(this.scrollToSelectedCommand);
      } else {
        // Give some time for dialog to close smoothly before resetting
        setTimeout(this.clearTemporarySettings, 300);
        setTimeout(this.reset, 300);
      }
    },
    commands() {
      this.selectedCommand = this.filteredCommands[0] || null;
    },
    searchQuery() {
      this.onSearchChanged();
    },
  },

  methods: {
    reset() {
      this.modalTitle = COMMAND_TITLE;
      this.searchPlaceholder = SEARCH_PLACEHOLDER;
      this.searchQuery = '';
      this.parentCommand = null;
      this.prepareCommands();
    },

    closeModal() {
      this.isModalOpen = false;
    },

    // Filters available commands based on search query and permissions.
    getFilteredCommands() {
      const query = this.normalizeQuery(this.searchQuery);
      const availableCommands = this.tempCommands.length
        ? this.tempCommands
        : this.commands;

      return availableCommands.filter((cmd) => {
        const command = clone(cmd);
        command.title = this.resolveCommandValue(command, 'title');

        if (this.parentCommand && command.pid !== this.parentCommand.id) {
          return false;
        }

        if (command.permission && !this.hasPermission(command.permission)) {
          return false;
        }

        if (command.if && !command.if()) {
          return false;
        }

        const searchTerms = [...(command.searchTerms || []), command.title]
          .join(' ')
          .toLowerCase();

        return (
          !query ||
          searchTerms.includes(query) ||
          this.matchTriggerWords(query, command.wordTriggers)
        );
      });
    },

    resolveCommandTitle(command) {
      if (
        typeof command.dynamicTitle === 'function' &&
        this.normalizeQuery(this.searchQuery)
      ) {
        return command.dynamicTitle(this.searchQuery);
      }
      return command.title || command.id;
    },

    // Normalize search query: lowercase, trim, and simplify.
    normalizeQuery(query) {
      return (
        query
          ?.toLowerCase()
          .trim()
          .replace(' is ', ' = ')
          .split('=')
          .pop()
          .trim() || ''
      );
    },

    // Check if any trigger words match the input query.
    matchTriggerWords(input, triggers = []) {
      if (!Array.isArray(triggers) || triggers.length === 0) return false;
      const regex = new RegExp(`\\b(${triggers.join('|')})\\b`, 'i');
      return regex.test(input) || triggers.some((t) => t.startsWith(input));
    },

    // Clean up and only show commands relevant to the current route.
    prepareCommands() {
      const currentRouteName = this.$route.name;
      this.commands = this.commands.filter(
        (cmd) =>
          !cmd.pid && (!cmd.routeName || cmd.routeName === currentRouteName)
      );
    },

    toggleModal() {
      if (this.isVectorRecruiting) return;
      this.isModalOpen = !this.isModalOpen;
    },

    // Navigate through the command list using up/down keys.
    navigate(direction) {
      const commands = this.filteredCommands;
      let currentIndex = this.selectedCommand
        ? commands.findIndex((cmd) => cmd.id === this.selectedCommand.id)
        : -1;

      let newIndex = Math.min(
        Math.max(currentIndex + direction, 0),
        commands.length - 1
      );
      this.selectedCommand = commands[newIndex];
      this.$nextTick(this.scrollToSelectedCommand);
    },

    // Scroll the container to ensure the selected command is visible.
    scrollToSelectedCommand() {
      const container = document.querySelector('.command-modal .v-card__text');
      if (!container) return;

      const active = container.querySelector('.active');
      if (!active) return;

      const { offsetTop: top, offsetHeight: height } = active;
      const containerHeight = container.clientHeight;

      container.scrollTop = top + height > containerHeight ? top - 160 : 0;
    },

    commandClasses(cmd) {
      return {
        active: this.selectedCommand?.id === cmd.id,
      };
    },

    selectCommand(command) {
      this.selectedCommand = command;
    },

    /**
     * Execute the currently selected command.
     */
    async executeSelectedCommand() {
      if (!this.selectedCommand?.run) return;
      const success = await this.selectedCommand.run(this.searchQuery);
      if (success === true) {
        this.closeModal();
      }
    },

    /**
     * Select and execute the clicked command.
     */
    async executeCommand(command) {
      this.selectCommand(command);
      await this.executeSelectedCommand();
    },

    resolveCommandValue(obj, key) {
      return typeof obj[key] === 'function' ? obj[key]() : obj[key];
    },

    // Dynamically register Vuex modules if not already registered.
    registerStoreModule(namespace, module) {
      if (this.$store.hasModule(namespace)) return false;
      this.$store.registerModule(namespace, module.create());
      return true;
    },

    // Initialize the default commands available globally.
    initCommands() {
      const commandInstances = [];

      registeredCommands.forEach((Cmd) => {
        const instance = new Cmd();
        instance.setVM(this);
        if (instance.init && !instance.init()) return;
        commandInstances.push(instance);
      });

      navigation.forEach((link) => {
        if (link.check && this.hasPermission(link.check)) return;
        if (link.disabled) return;

        const navCommand = new Command({
          id: link.path,
          title: link.text,
          icon: `sidebar/${link.icon}`,
          run() {
            this.vm.$router.push(link.path);
            return true;
          },
        });
        navCommand.setVM(this);
        commandInstances.push(navCommand);
      });

      this.commands = commandInstances;
    },

    // Dynamically add commands to the list.
    // `temp` determines if they should be removed when modal closes.
    addCommands(newCommands, action = 'prepend', isTemporary = true) {
      const updatedCommands = [...this.commands];
      const method = action === 'prepend' ? 'unshift' : 'push';

      newCommands.forEach((cmdData) => {
        const command = cmdData.isCommand ? cmdData : new Command(cmdData);
        if (isTemporary) command.routeName = this.$route.name;

        command.setVM(this);
        if (command.init && !command.init()) return;

        const filtered = updatedCommands.filter(
          (existing) => existing.id !== command.id
        );
        filtered[method](command);
        Object.assign(updatedCommands, filtered);
      });

      this.commands = updatedCommands;
    },

    // Clear temporary (route-specific) commands and search.
    clearTemporarySettings(data = {}) {
      const { tempCommands = [], search = '' } = data;
      this.tempCommands = tempCommands;
      this.searchQuery = search;
    },

    // Initialize OmniSearch (used when no direct match is found).
    initSearchHandler() {
      const searchCmd = new OmniSearch();
      searchCmd.setVM(this);
      if (searchCmd.init) searchCmd.init();
      this.searchHandler = searchCmd;
    },

    // Handle changes to search query.
    // If no direct match, fallback to OmniSearch.
    onSearchChanged() {
      if (this.parentCommand?.asParent) {
        return this.parentCommand.asParent();
      }
      this.selectedCommand = this.filteredCommands[0] || null;
      if (!this.selectedCommand) {
        this.searchHandler.run();
        this.onSearchChanged();
      }
    },

    handlePaste() {
      setTimeout(this.onSearchChanged, 100);
    },

    //Listen to global key combination (CMD+K or CTRL+K) to open modal.
    handleGlobalKeydown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        this.toggleModal();
      }
    },

    checkCySearchQuery(data) {
      const search = data?.search?.trim();
      if (!search) return false;

      const askCyCommand = this.commands.find(
        (cmd) => cmd.id === 'openChatBot'
      );
      if (!askCyCommand) return false;

      if (this.matchTriggerWords(search, askCyCommand.wordTriggers)) {
        askCyCommand.run(search);
        return true;
      }

      return false;
    },

    handleCommandOpen(data) {
      if (this.isVectorRecruiting) return false;
      if (this.checkCySearchQuery(data)) return false;

      this.isModalOpen = true;
      this.clearTemporarySettings(data);
    },
  },

  mounted() {
    this.initCommands();
    EventBus.$on('openCommandModal', this.handleCommandOpen);
  },

  created() {
    setCommandInstance(this);
    this.initSearchHandler();
    window.cmdk = this;
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
  },
};
</script>

<style lang="less">
.command-content__search {
  position: sticky;
  top: 0;
  z-index: 1000;
  padding: 16px 16px 8px;
  box-shadow: @base-shadow;
  background: #fff;

  .v-progress-linear {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    z-index: 1;
  }
}

.command-content__list {
  margin: 16px;
  box-shadow: @base-shadow;
}

.command-modal {
  .v-card__title {
    background: var(--gradient) !important;
  }
}

.command-search .v-input__slot {
  padding: 0 16px !important;
  min-height: 48px !important;
}

.command {
  cursor: pointer;
  border-top: 1px solid #fff;
  border-bottom: 1px solid #fff;
  border-radius: 3px;

  &.active {
    background-color: var(--v-lightGray-base);
    border-color: rgba(0, 0, 0, 0.1);
  }
}
</style>
